import { api } from '@/lib/api';

export async function loadFlightTrailHistory(
  droneId: string,
  trailHistory: Map<string, [number, number][]>,
): Promise<void> {
  try {
    const res = await api.get<{ data: { id: string; status: string; drone_id: string }[] }>('/api/flights?limit=50');
    const flightsByDrone = new Map<string, { id: string; status: string }>();
    for (const f of res.data) {
      if (f.status !== 'in_progress' && f.status !== 'completed') continue;
      const existing = flightsByDrone.get(f.drone_id);
      if (!existing) flightsByDrone.set(f.drone_id, f);
    }
    const flight = flightsByDrone.get(droneId);
    if (!flight) return;

    const telRes = await api.get<{ data: { latitude: number; longitude: number }[] }>(
      `/api/flights/${flight.id}/telemetry`,
    );
    if (telRes.data.length === 0) return;

    const dbCoords: [number, number][] = telRes.data.map((t) => [t.longitude, t.latitude]);
    const existing = trailHistory.get(droneId) || [];
    trailHistory.set(droneId, [...dbCoords, ...existing]);
  } catch {
    // Flight history not available - live trail only
  }
}

const AUTO_TRAIL_MAX = 30;

/**
 * Minimum ground distance before a new trail breadcrumb is recorded.
 *
 * A GPS position is never numerically identical twice, so comparing
 * `lat/lng !== previous` appends a point on EVERY sample - including for an
 * aircraft sitting still on the ground. Measured on the SkyPulse airframe
 * 2026-08-06: parked and disarmed, 31 fixes over 30 s wandered 4.5 m with a
 * 0.33 m mean step, and the COP drew a trail that made a stationary aircraft
 * look like it was crawling around the site.
 *
 * 2 m sits above that scatter (metre-level even on a good fix, several metres
 * on a marginal one) and well below any real movement worth drawing.
 */
const MIN_TRAIL_STEP_M = 2;

/** Ground distance in metres. Equirectangular - exact enough well below 1 km. */
function metresBetween(lon1: number, lat1: number, lon2: number, lat2: number): number {
  const EARTH_RADIUS_M = 6371000;
  const midLatRad = ((lat1 + lat2) / 2) * (Math.PI / 180);
  const x = (lon2 - lon1) * (Math.PI / 180) * Math.cos(midLatRad);
  const y = (lat2 - lat1) * (Math.PI / 180);
  return Math.sqrt(x * x + y * y) * EARTH_RADIUS_M;
}

/** True when the aircraft has actually moved, rather than the fix having jittered. */
function hasMovedEnough(
  last: [number, number] | undefined,
  longitude: number,
  latitude: number,
): boolean {
  if (!last) return true;
  return metresBetween(last[0], last[1], longitude, latitude) >= MIN_TRAIL_STEP_M;
}

export function appendLiveTrailPoints(
  drones: { drone_id: string; longitude: number; latitude: number }[],
  trailDroneIds: Set<string>,
  trailHistory: Map<string, [number, number][]>,
): void {
  for (const d of drones) {
    if (!trailDroneIds.has(d.drone_id)) continue;
    const hist = trailHistory.get(d.drone_id) || [];
    if (hasMovedEnough(hist[hist.length - 1], d.longitude, d.latitude)) {
      hist.push([d.longitude, d.latitude]);
      trailHistory.set(d.drone_id, hist);
    }
  }
}

export function appendAutoTrailPoints(
  drones: { drone_id: string; longitude: number; latitude: number; speed: number; status: string }[],
  autoTrailHistory: Map<string, [number, number][]>,
): void {
  const activeDroneIds = new Set<string>();
  for (const d of drones) {
    activeDroneIds.add(d.drone_id);
    if (d.speed <= 0 && d.status !== 'flying') continue;
    const hist = autoTrailHistory.get(d.drone_id) || [];
    // Distance gate, not `speed > 0`: a parked aircraft reports 0.02-0.8 m/s of
    // EKF noise, which clears that check and drew a trail for a stationary drone.
    if (hasMovedEnough(hist[hist.length - 1], d.longitude, d.latitude)) {
      hist.push([d.longitude, d.latitude]);
      if (hist.length > AUTO_TRAIL_MAX) hist.splice(0, hist.length - AUTO_TRAIL_MAX);
      autoTrailHistory.set(d.drone_id, hist);
    }
  }
  for (const id of autoTrailHistory.keys()) {
    if (!activeDroneIds.has(id)) autoTrailHistory.delete(id);
  }
}
